import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Flex,
} from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import { useLocales, useTranslationsState, validateNameTranslations } from "utils/translations";
import LocaleNameTabs from "components/localeNameFields/LocaleNameTabs";

const LIVE_TV_PAGE_ID = PAGE_IDS.LIVE_TV;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyNameTranslation = () => ({ name: "" });

function CreateLiveChannel({ onCreated }) {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const [translations, setTranslations, setTranslationField] = useTranslationsState(locales, emptyNameTranslation);
  const [slug, setSlug] = useState("");
  const [streamType, setStreamType] = useState("DIRECT");
  const [streamUrl, setStreamUrl] = useState("");
  const [icon, setIcon] = useState("");
  const [color, setColor] = useState("");
  const [order, setOrder] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validateNameTranslations(translations, locales, t);
    if (!streamUrl.trim()) {
      errors.streamUrl = t("Stream URL is required");
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const translationsPayload = locales
        .map(({ code }) => ({ locale: code, name: (translations[code]?.name || "").trim() }))
        .filter((tr) => tr.name);

      const data = {
        page_id: LIVE_TV_PAGE_ID,
        translations: translationsPayload,
        ...(slug.trim() && { slug: slug.trim() }),
        streamType,
        streamUrl: streamUrl.trim(),
        ...(icon.trim() && { icon: icon.trim() }),
        ...(color.trim() && { color: color.trim() }),
        ...(order !== "" && { order: Number(order) }),
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/live-channel`,
        "POST",
        data
      );
      if (!response) {
        throw new Error("Failed to create live channel");
      }
      setTranslations(() => {
        const reset = {};
        locales.forEach(({ code }) => { reset[code] = emptyNameTranslation(); });
        return reset;
      });
      setSlug("");
      setStreamType("DIRECT");
      setStreamUrl("");
      setIcon("");
      setColor("");
      setOrder("");
      onCreated?.();
    } catch (err) {
      setError(err.message || t("Failed to create live channel. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <LocaleNameTabs
          locales={locales}
          translations={translations}
          errors={fieldErrors}
          onChange={(code, value) => setTranslationField(code, "name", value)}
          borderColor={GOLD_BORDER}
        />

        <FormControl mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Slug")}</FormLabel>
          <Input placeholder={t("Leave blank to auto-generate from the name")} value={slug} onChange={(e) => setSlug(e.target.value)} {...fieldProps} />
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt="16px">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Stream Type")}</FormLabel>
            <Select value={streamType} onChange={(e) => setStreamType(e.target.value)} {...fieldProps}>
              <option value="DIRECT">{t("Direct stream (native player)")}</option>
              <option value="IFRAME">{t("Iframe embed")}</option>
            </Select>
          </FormControl>
          <FormControl isInvalid={!!fieldErrors.streamUrl}>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Stream URL")}</FormLabel>
            <Input
              placeholder={streamType === "DIRECT" ? "https://.../stream.m3u8" : "https://.../embed/..."}
              value={streamUrl}
              onChange={(e) => setStreamUrl(e.target.value)}
              {...fieldProps}
            />
          </FormControl>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt="16px">
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Icon")}</FormLabel>
            <Input placeholder="fa-solid fa-satellite-dish" value={icon} onChange={(e) => setIcon(e.target.value)} {...fieldProps} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Color")}</FormLabel>
            <Flex align="center" gap={2}>
              <Input type="color" value={color || "#C7B45E"} onChange={(e) => setColor(e.target.value)} w="56px" p="4px" {...fieldProps} />
              <Input placeholder="#RRGGBB" value={color} onChange={(e) => setColor(e.target.value)} {...fieldProps} />
            </Flex>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Order")}</FormLabel>
            <Input type="number" placeholder="0" value={order} onChange={(e) => setOrder(e.target.value)} {...fieldProps} />
          </FormControl>
        </SimpleGrid>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mt="20px"
          mb="8px"
          isLoading={isSubmitting}
        >
          {t("Create Channel")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {error}
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateLiveChannel;
